import { useLocation } from '@solidjs/router'
import { type Component, createEffect, createMemo, createSignal, For, type ParentProps, Show } from 'solid-js'
import Avatar from '~/components/Avatar'
import Button from '~/components/Button'
import Drawer from '~/components/Drawer'
import Icon from '~/components/Icon'
import Menu from '~/components/Menu'
import SidebarMenu from '~/components/SidebarMenu'
import { useAuth } from '~/contexts/AuthContext'
import { useCompany } from '~/contexts/CompanyContext'
import { usePermissions } from '~/hooks/usePermissions'
import ConnectionBoundary from '~/layers/ConnectionBoundary'
import NotificationsDrawer from '~/layers/NotificationsDrawer'
import ThemeToggle from '~/layers/ThemeToggle'
import EsperandoEmpresaPage from '~/pages/admin/esperando-empresa'
import { type Path, useNavigate } from '~/router'
import { useTrailOverride } from '~/stores/breadcrumb'
import { setOffline, setUnauthorized } from '~/stores/connection'
import type { SidebarMenuSectionType } from '~/types/SidebarMenu'
import { P, type PermissionName } from '~/utils/permissions'

/**
 * Todo el panel cuelga de /admin, separado de la landing pública. El layout es
 * el marco: navegación a la izquierda, barra superior con la ruta actual y el
 * contenido debajo. Las páginas sólo ponen su contenido.
 */
const AdminLayout: Component<ParentProps> = props => {
  const location = useLocation()
  const auth = useAuth()
  const navigate = useNavigate()
  const { currentCompany, companies, hasMultipleCompanies, switchCompany } = useCompany()
  const { isRoot, can } = usePermissions()

  const [collapsed, setCollapsed] = createSignal(false)
  const [mobileOpen, setMobileOpen] = createSignal(false)
  const [notificationsOpen, setNotificationsOpen] = createSignal(false)

  /**
   * Usuario sin empresa asignada: sólo puede ver su perfil. Cualquier otra
   * ruta del panel se reemplaza por la pantalla de espera (sin sidebar ni
   * ConnectionBoundary, para que un 403 de otra página no la tape). El backend
   * además rechaza con 403 los endpoints que exigen empresa activa.
   */
  const sinEmpresa = createMemo(() => !currentCompany().id && !isRoot())
  const mostrarEspera = createMemo(() => sinEmpresa() && location.pathname !== '/admin/perfil')

  /**
   * Al navegar entre páginas del panel se resetean los estados de conexión y
   * de "sin acceso": cada página decide por sí misma con sus peticiones. Sin
   * esto, un 401 puntual dejaba la pantalla congelada en "Sin acceso" para
   * todas las rutas siguientes (el contenido desmontado nunca volvía a pedir).
   */
  createEffect(() => {
    void location.pathname
    setOffline(false)
    setUnauthorized(false)
  })

  /**
   * Mapa de rutas a permisos requeridos. Se usa para filtrar el menú y para
   * mostrar un mensaje de "sin acceso" si el usuario entra directamente a una
   * ruta sin permiso.
   */
  const routePermissions: Record<string, PermissionName | '' | { root: true }> = {
    '/admin': '',
    '/admin/crm': P.crm.read,
    '/admin/clientes': P.customers.read,
    '/admin/servicios': P.products.read,
    '/admin/inventario': P.inventory.read,
    '/admin/cotizaciones': P.quotes.read,
    '/admin/cuentas-cobro': P.paymentAccounts.read,
    '/admin/pedidos': P.orders.read,
    '/admin/prospectos': P.leads.read,
    '/admin/automatizaciones': P.workflows.read,
    '/admin/chat': P.ai.chat,
    '/admin/perfil': '',
    '/admin/empresas': { root: true },
    '/admin/usuarios': P.users.read,
    '/admin/roles': P.roles.read,
    '/admin/parametros': '',
    '/admin/builder/paginas': P.pages.read,
    '/admin/formularios': P.forms.read,
    '/admin/configuracion': { root: true },
    '/admin/configuracion/ia': { root: true },
    '/admin/configuracion/correo': { root: true },
    '/admin/configuracion/auditoria': { root: true },
    // Las pasarelas SÍ son configurables por empresa (el ámbito de sistema se
    // gestiona dentro de la pantalla, y ése sí es sólo de root).
    '/admin/configuracion/pagos': P.paymentGateways.read
  }

  /** Verifica si el usuario tiene acceso a una ruta. */
  const hasRouteAccess = (href: string): boolean => {
    const perm = routePermissions[href]
    if (perm === undefined) return true // Rutas no mapeadas: permitidas por defecto
    if (perm === '') return true // Dashboard: solo requiere estar autenticado
    if (typeof perm === 'object' && 'root' in perm) return isRoot()
    return can(perm)
  }

  /** Permiso requerido para la ruta actual (para el guard). */
  const currentRoutePermission = createMemo(() => {
    const pathname = location.pathname
    // Buscar la ruta más específica que coincida
    const exact = routePermissions[pathname]
    if (exact !== undefined) return exact
    // Para rutas con parámetros (ej: /admin/builder/paginas/123), buscar el prefijo
    for (const [route, perm] of Object.entries(routePermissions)) {
      if (pathname.startsWith(route + '/')) return perm
    }
    return '' // Sin restricción
  })

  /** Si el usuario no tiene acceso a la ruta actual. */
  const noAccess = createMemo(() => {
    const perm = currentRoutePermission()
    if (perm === '') return false
    if (typeof perm === 'object' && 'root' in perm) return !isRoot()
    return !can(perm)
  })

  const sections = createMemo<SidebarMenuSectionType[]>(() => {
    /**
     * Sin empresa asignada: el único destino permitido es el perfil. El menú
     * se reduce a esa entrada para no tentar navegar a secciones que el
     * backend rechaza con 403.
     */
    if (sinEmpresa()) {
      return [
        {
          title: 'MENÚ',
          items: [{ key: 'perfil', label: 'Perfil', icon: 'material-symbols-light:person-outline', href: '/admin/perfil' }]
        }
      ]
    }

    return [
      {
        title: 'MENÚ',
        items: [
          { key: 'inicio', label: 'Dashboard', icon: 'material-symbols-light:space-dashboard-outline', href: '/admin' },
          // CRM agrupa el embudo comercial: el tablero, la ficha de clientes y los
          // prospectos que llegan de la web. Van dentro para no duplicar entradas.
          ...(hasRouteAccess('/admin/crm') || hasRouteAccess('/admin/clientes')
            ? [
                {
                  key: 'crm',
                  label: 'CRM',
                  icon: 'material-symbols-light:view-kanban-outline',
                  href: '/admin/crm',
                  children: [
                    ...(hasRouteAccess('/admin/crm') ? [{ key: 'crm-tablero', label: 'Tablero', href: '/admin/crm', dotColor: 'purple' as const }] : []),
                    ...(hasRouteAccess('/admin/clientes') ? [{ key: 'crm-clientes', label: 'Clientes', href: '/admin/clientes', dotColor: 'blue' as const }] : []),
                    ...(hasRouteAccess('/admin/prospectos') ? [{ key: 'crm-prospectos', label: 'Prospectos', href: '/admin/prospectos', dotColor: 'green' as const }] : [])
                  ]
                }
              ]
            : []),
          ...(hasRouteAccess('/admin/servicios') ? [{ key: 'servicios', label: 'Catálogo', icon: 'material-symbols-light:inventory-2-outline', href: '/admin/servicios' }] : []),
          ...(hasRouteAccess('/admin/inventario') ? [{ key: 'inventario', label: 'Inventario', icon: 'material-symbols-light:warehouse-outline', href: '/admin/inventario' }] : []),
          ...(hasRouteAccess('/admin/cotizaciones') ? [{ key: 'cotizaciones', label: 'Cotizaciones', icon: 'material-symbols-light:description-outline', href: '/admin/cotizaciones' }] : []),
          ...(hasRouteAccess('/admin/cuentas-cobro') ? [{ key: 'cuentas-cobro', label: 'Cuentas de Cobro', icon: 'material-symbols-light:receipt-outline', href: '/admin/cuentas-cobro' }] : []),
          ...(hasRouteAccess('/admin/pedidos') ? [{ key: 'pedidos', label: 'Pedidos', icon: 'material-symbols-light:shopping-cart-outline', href: '/admin/pedidos' }] : []),
          ...(hasRouteAccess('/admin/automatizaciones')
            ? [{ key: 'automatizaciones', label: 'Automatizaciones', icon: 'material-symbols-light:account-tree-outline', href: '/admin/automatizaciones' }]
            : []),
          // Para root la entrada vive en SISTEMA > Configuración, junto a Correo e
          // IA; aquí sólo se ofrece a quien no la vería allí.
          ...(!isRoot() && hasRouteAccess('/admin/configuracion/pagos')
            ? [{ key: 'pasarelas', label: 'Pasarelas de pago', icon: 'material-symbols-light:credit-card-outline', href: '/admin/configuracion/pagos' }]
            : []),
          ...(hasRouteAccess('/admin/chat') ? [{ key: 'chat', label: 'Chat IA', icon: 'material-symbols-light:chat-outline', href: '/admin/chat' }] : [])
        ]
      },
      {
        title: 'ADMINISTRACIÓN',
        items: [
          ...(hasRouteAccess('/admin/empresas') ? [{ key: 'empresas', label: 'Empresas', icon: 'material-symbols-light:domain', href: '/admin/empresas' }] : []),
          ...(hasRouteAccess('/admin/usuarios') ? [{ key: 'usuarios', label: 'Usuarios', icon: 'material-symbols-light:badge-outline', href: '/admin/usuarios' }] : []),
          ...(hasRouteAccess('/admin/roles') ? [{ key: 'roles', label: 'Roles y Permisos', icon: 'material-symbols-light:shield-outline', href: '/admin/roles' }] : []),
          ...(hasRouteAccess('/admin/parametros') ? [{ key: 'parametros', label: 'Parámetros', icon: 'material-symbols-light:tune', href: '/admin/parametros' }] : []),
          // Builder: mostrar solo si tiene acceso a páginas o formularios
          ...(hasRouteAccess('/admin/builder/paginas') || hasRouteAccess('/admin/formularios')
            ? [
                {
                  key: 'builder',
                  label: 'Builder',
                  icon: 'material-symbols-light:construction',
                  href: '/admin/builder/paginas',
                  children: [
                    ...(hasRouteAccess('/admin/formularios') ? [{ key: 'builder-forms', label: 'Formularios', href: '/admin/formularios', dotColor: 'blue' as const }] : []),
                    ...(hasRouteAccess('/admin/builder/paginas') ? [{ key: 'builder-pages', label: 'Páginas', href: '/admin/builder/paginas', dotColor: 'purple' as const }] : [])
                  ]
                }
              ]
            : [])
        ]
      },
      // Configuración: solo root
      ...(isRoot()
        ? [
            {
              title: 'SISTEMA',
              items: [
                {
                  key: 'config',
                  label: 'Configuración',
                  icon: 'material-symbols-light:settings-outline',
                  children: [
                    { key: 'config-general', label: 'General', href: '/admin/configuracion', dotColor: 'blue' as const },
                    { key: 'config-ia', label: 'IA', href: '/admin/configuracion/ia', dotColor: 'purple' as const },
                    { key: 'config-correo', label: 'Correo', href: '/admin/configuracion/correo', dotColor: 'green' as const },
                    { key: 'config-pagos', label: 'Pasarelas de pago', href: '/admin/configuracion/pagos', dotColor: 'orange' as const },
                    { key: 'config-auditoria', label: 'Auditoría', href: '/admin/configuracion/auditoria', dotColor: 'red' as const }
                  ]
                }
              ]
            }
          ]
        : [])
    ]
  })

  /**
   * Para el menú: un ítem se activa si la ruta coincide exactamente o si es
   * padre de la ruta actual (ej: /admin/cuentas-cobro se activa en
   * /admin/cuentas-cobro/nueva). El root /admin solo coincide exacto.
   */
  const isActive = (href: string) => {
    if (href === '/admin') return location.pathname === '/admin'
    return location.pathname === href || location.pathname.startsWith(`${href}/`)
  }

  /** Para migas de pan: match exacto para no confundir /admin/configuracion con /admin/configuracion/ia. */
  const isExactActive = (href: string) => location.pathname === href

  /** Migas de pan derivadas del menú: ítem › sub-ítem. Para páginas anidadas
   *  (una ficha, un editor) muestra la sección padre: ej. /admin/empresas/123 → Empresas.
   *  La propia página puede fijar sus migas (ej. ficha de empresa) con setTrailOverride. */
  const trail = createMemo(() => {
    const override = useTrailOverride()
    if (override && override.length > 0) return override
    for (const section of sections()) {
      for (const item of section.items) {
        const activeChild = item.children?.find(child => child.href && isExactActive(child.href))
        // Sin match exacto, el hijo de mayor prefijo (la ficha /admin/clientes/2
        // cuelga de /admin/clientes, no del propio ítem /admin/crm).
        if (!activeChild && item.children) {
          const byPrefix = item.children
            .filter(child => child.href && child.href !== '/admin' && location.pathname.startsWith(`${child.href}/`))
            .sort((a, b) => (b.href?.length ?? 0) - (a.href?.length ?? 0))[0]
          if (byPrefix) return [item.label, byPrefix.label]
        }
        if (activeChild) return [item.label, activeChild.label]
        if (item.href && isExactActive(item.href)) return [item.label]
        if (item.href && item.href !== '/admin' && location.pathname.startsWith(`${item.href}/`)) return [item.label]
      }
    }
    return ['Panel']
  })

  const handleLogout = () => {
    auth.logout()
    navigate('/')
  }

  const go = (path: Path) => {
    setMobileOpen(false)
    navigate(path)
  }

  const handleSwitchCompany = async (companyId: string) => {
    if (companyId === currentCompany().id) return
    await switchCompany(companyId)
    navigate('/admin')
  }

  const companyItems = () =>
    companies().map(c => ({
      label: c.company.name,
      icon: c.company.id === currentCompany().id ? 'material-symbols-light:check' : undefined,
      onClick: () => handleSwitchCompany(c.company.id)
    }))

  /**
   * Función y no una variable JSX: el mismo nodo no puede vivir en las dos ramas
   * de un `Show`. Compartirlo mueve el nodo de sitio y descoloca el árbol (se
   * llegaba a cambiar hasta el título de la pestaña al contraer el menú).
   */
  const logo = () => (
    <div class="p-2 rounded-xl bg-linear-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/25">
      <Icon icon="edens:logo" class="text-white" size={22} />
    </div>
  )

  const brand = (
    <div class="flex items-center gap-3">
      {/* Contraído, el logo es el que abre el cambio de empresa: el nombre y su
          desplegable no caben, y sin esto no había forma de cambiar de empresa
          sin volver a expandir el menú. */}
      <Show when={collapsed() && hasMultipleCompanies()} fallback={logo()}>
        <Menu
          trigger={
            <div class="relative cursor-pointer" title={`${currentCompany().name ?? 'Empresa'} — cambiar de empresa`}>
              {logo()}
              <span class="absolute -bottom-0.5 -right-0.5 rounded-full bg-surface p-px">
                <Icon icon="material-symbols-light:expand-more" size={12} class="text-foreground-subtle block" />
              </span>
            </div>
          }
          items={companyItems()}
        />
      </Show>

      <Show when={!collapsed()}>
        <div class="min-w-0">
          <p class="text-base font-bold text-foreground leading-tight truncate">Florez Jaramillo</p>
          <Show when={hasMultipleCompanies()} fallback={<p class="text-xs text-foreground-subtle truncate">{currentCompany().name ?? 'Panel de administración'}</p>}>
            <Menu
              trigger={
                <div class="flex items-center gap-1 cursor-pointer rounded-md hover:bg-surface-container px-1 -mx-1 py-0.5 transition-colors">
                  <p class="text-xs text-primary font-medium truncate">{currentCompany().name ?? 'Seleccionar empresa'}</p>
                  <Icon icon="material-symbols-light:expand-more" size={14} class="text-foreground-subtle shrink-0" />
                </div>
              }
              items={companyItems()}
            />
          </Show>
        </div>
      </Show>
    </div>
  )

  const userCard = (
    <Menu
      trigger={
        <div class="flex items-center gap-3 w-full cursor-pointer rounded-xl p-1 hover:bg-surface-container transition-colors">
          <Avatar alt={String(auth.user()?.name || 'A')} size="sm" />
          <Show when={!collapsed()}>
            <div class="flex-1 min-w-0 text-left">
              <p class="text-sm font-medium text-foreground truncate">{String(auth.user()?.name || 'Admin')}</p>
              <p class="text-xs text-foreground-subtle truncate">{String(auth.user()?.email || '')}</p>
            </div>
            <Icon icon="material-symbols-light:more-vert" size={18} class="text-foreground-subtle shrink-0" />
          </Show>
        </div>
      }
      items={[
        { label: 'Mi Perfil', icon: 'material-symbols-light:person-outline', onClick: () => go('/admin/perfil') },
        ...(isRoot() ? [{ label: 'Configuración', icon: 'material-symbols-light:settings-outline', onClick: () => go('/admin/configuracion') }] : []),
        ...(sinEmpresa() ? [] : [{ label: 'Ver sitio público', icon: 'material-symbols-light:open-in-new', onClick: () => go('/') }]),
        { label: 'Cerrar sesión', icon: 'material-symbols-light:logout', color: 'danger' as const, onClick: handleLogout }
      ]}
    />
  )

  const sidebar = (
    <SidebarMenu
      sections={sections()}
      collapsed={collapsed()}
      onToggleCollapse={() => setCollapsed(!collapsed())}
      onNavigate={() => setMobileOpen(false)}
      header={brand}
      footer={userCard}
      isActive={isActive}
    />
  )

  return (
    <Show when={!mostrarEspera()} fallback={<EsperandoEmpresaPage />}>
      <div class="h-dvh flex overflow-hidden bg-surface-container">
        {/* Navegación fija en escritorio */}
        <div class={`hidden lg:block shrink-0 transition-all duration-200 ${collapsed() ? 'w-20' : 'w-64'}`}>{sidebar}</div>

        {/* Navegación en cajón para móvil */}
        <Drawer isOpen={mobileOpen()} onClose={() => setMobileOpen(false)} position="left" size="sm">
          <div class="h-full">{sidebar}</div>
        </Drawer>

        <main class="flex-1 flex flex-col overflow-hidden">
          <header class="h-16 shrink-0 border-b border-border bg-surface flex items-center justify-between gap-4 px-4 sm:px-6">
            <div class="flex items-center gap-2 min-w-0">
              <Button variant="text" color="default" class="lg:hidden p-2! h-9 w-9" onClick={() => setMobileOpen(true)} aria-label="Abrir menú" icon="material-symbols-light:menu" iconSize={22} />
              <nav aria-label="Ruta actual" class="flex items-center gap-1.5 min-w-0">
                <For each={trail()}>
                  {(step, index) => (
                    <>
                      <Show when={index() > 0}>
                        <Icon icon="material-symbols-light:chevron-right" size={16} class="text-foreground-subtle shrink-0" />
                      </Show>
                      <span class={index() === trail().length - 1 ? 'text-sm font-semibold text-foreground truncate' : 'text-sm text-foreground-subtle truncate'}>{step}</span>
                    </>
                  )}
                </For>
              </nav>
            </div>

            <div class="flex items-center gap-1">
              <Show when={!sinEmpresa()}>
                <Button
                  variant="text"
                  color="default"
                  class="hidden sm:inline-flex p-2! h-9 w-9"
                  onClick={() => window.open('/', '_blank', 'noopener')}
                  tooltip="Ver sitio público"
                  aria-label="Ver sitio público"
                  icon="material-symbols-light:public"
                  iconSize={22}
                />
              </Show>
              <Button
                variant="text"
                color="default"
                class="p-2! h-9 w-9"
                onClick={() => setNotificationsOpen(true)}
                tooltip="Notificaciones"
                aria-label="Notificaciones"
                icon="material-symbols-light:notifications-outline"
                iconSize={22}
              />
              <ThemeToggle />
            </div>
          </header>

          <div class="flex-1 overflow-y-auto">
            <Show
              when={!noAccess()}
              fallback={
                <div class="flex flex-col items-center justify-center h-full text-center">
                  <Icon icon="material-symbols-light:lock-outline" size={64} class="text-foreground-subtle mb-4" />
                  <h2 class="text-xl font-semibold text-foreground mb-2">Sin acceso</h2>
                  <p class="text-foreground-subtle mb-6">No tienes permiso para ver esta sección.</p>
                  <Button onClick={() => navigate('/admin')} prependIcon="material-symbols-light:home-outline">
                    Volver al inicio
                  </Button>
                </div>
              }
            >
              <ConnectionBoundary>{props.children}</ConnectionBoundary>
            </Show>
          </div>
        </main>

        <NotificationsDrawer isOpen={notificationsOpen()} onClose={() => setNotificationsOpen(false)} />
      </div>
    </Show>
  )
}

export default AdminLayout
